import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { translate } from '@jsverse/transloco';
import { Observable, shareReplay } from 'rxjs';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { IGridResult, IGridState } from 'src/app/core/models/base';
import { IEmployee } from 'src/app/employee/employee';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import {
  ISearchWorkTimeViolationsRequest,
  IWorkTimeViolation,
  WorkTimeViolationStatus,
  WorkTimeViolationType
} from '../../models/work-time-violation';
import { TimeEntryService } from '../../services/time-entry.service';
import { WorkTimeViolationService } from '../../services/work-time-violation.service';

// Row model for the grid: the TimeSpan strings are shortened to hh:mm once per load.
interface IWorkTimeViolationRow extends IWorkTimeViolation {
  workTimeInWindowShort: string;
  effectiveBreakShort: string;
  missingBreakShort: string;
  windowShort: string;
  entryStartShort: string;
}

@UntilDestroy()
@Component({
    selector: 'app-work-time-violations',
    templateUrl: './work-time-violations.component.html',
    standalone: false
})
export class WorkTimeViolationsComponent implements OnInit {

  constructor(
    private workTimeViolationService: WorkTimeViolationService,
    private timeEntryService: TimeEntryService,
    private fb: UntypedFormBuilder,
    private permissionStorage: PermissionStorageService,
    private appNotificationService: AppNotificationService
  ) { }

  WorkTimeViolationType = WorkTimeViolationType;
  WorkTimeViolationStatus = WorkTimeViolationStatus;

  gridData: IGridResult<IWorkTimeViolationRow[]> = {
    data: [],
    total: 0
  };
  gridState: IGridState = { pageSize: 25, skip: 0 };

  // Only the employees whose time data the current user may see — the server refuses the rest.
  employees$: Observable<IEmployee[]> =
    this.timeEntryService.getVisibleEmployees().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  // Default items without a value send no filter; their label is rendered by the template.
  employeeDefaultItem = { id: null, firstName: null, lastName: null };
  typeDefaultItem = { value: null };
  statusDefaultItem = { value: null };
  typeOptions = [
    { value: WorkTimeViolationType.LunchBreak },
    { value: WorkTimeViolationType.UnstoppedTimeEntry }
  ];
  statusOptions = [
    { value: WorkTimeViolationStatus.Open },
    { value: WorkTimeViolationStatus.Corrected },
    { value: WorkTimeViolationStatus.Cleared }
  ];

  // Open violations are what a supervisor has to act on, so they are the default view.
  filterForm = this.fb.group({
    employee: [null],
    dateRangeStart: [],
    dateRangeEnd: [],
    type: [null],
    status: [WorkTimeViolationStatus.Open]
  });

  searchRequest: ISearchWorkTimeViolationsRequest = {
    pageNumber: 1,
    pageSize: 25,
    status: WorkTimeViolationStatus.Open,
    orderBy: ['Date desc']
  };

  // Settling a violation is a supervisor decision, guarded like signing a subordinate's day.
  canClear = (this.permissionStorage.getPermissions() ?? [])
    .includes('Permissions.DayReportingObject.SignEntry');

  violationToClear: IWorkTimeViolation | null = null;
  clearForm = new UntypedFormGroup({
    remark: new UntypedFormControl(null, Validators.required)
  });

  ngOnInit(): void {
    this.updateList();
  }

  updateList() {
    this.workTimeViolationService.searchWorkTimeViolations(this.searchRequest)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.gridData.data = response.data.map(violation => ({
          ...violation,
          workTimeInWindowShort: this.toShort(violation.workTimeInWindow),
          effectiveBreakShort: this.toShort(violation.effectiveBreak),
          missingBreakShort: this.toShort(violation.missingBreak),
          windowShort: `${this.toShort(violation.windowStart)} - ${this.toShort(violation.windowEnd)}`,
          entryStartShort: this.toShort(violation.entryStart)
        }));
        this.gridData.total = response.totalCount;
      });
  }

  onSubmitFilter() {
    const formValues = this.filterForm.value;

    // Dates leave the picker at local midnight; shift them so the backend receives the calendar day.
    this.searchRequest.from = formValues.dateRangeStart
      ? new Date(formValues.dateRangeStart.getTime() - formValues.dateRangeStart.getTimezoneOffset() * 60000)
      : null;
    this.searchRequest.to = formValues.dateRangeEnd
      ? new Date(formValues.dateRangeEnd.getTime() - formValues.dateRangeEnd.getTimezoneOffset() * 60000)
      : null;
    this.searchRequest.employeeIds = formValues.employee?.id ? [formValues.employee.id] : null;
    this.searchRequest.type = formValues.type ?? null;
    this.searchRequest.status = formValues.status ?? null;
    this.resetPaging();

    this.updateList();
  }

  onResetFilter() {
    this.filterForm.reset({ status: WorkTimeViolationStatus.Open });

    this.searchRequest.from = null;
    this.searchRequest.to = null;
    this.searchRequest.employeeIds = null;
    this.searchRequest.type = null;
    this.searchRequest.status = WorkTimeViolationStatus.Open;
    this.resetPaging();

    this.updateList();
  }

  onPageChange({ skip, take }: PageChangeEvent) {
    this.gridState.skip = skip;
    this.gridState.pageSize = take;
    this.searchRequest.pageNumber = Math.round((skip + take) / take);
    this.searchRequest.pageSize = take;

    this.updateList();
  }

  typeName(type: WorkTimeViolationType): string {
    return WorkTimeViolationType[type];
  }

  statusName(status: WorkTimeViolationStatus): string {
    return WorkTimeViolationStatus[status];
  }

  // Reuses the expense status colours: open needs attention, corrected is resolved by the
  // employee, cleared is a supervisor decision.
  statusClass(status: WorkTimeViolationStatus): string {
    switch (status) {
      case WorkTimeViolationStatus.Corrected: return 'status-accepted';
      case WorkTimeViolationStatus.Cleared: return 'status-submitted';
      default: return 'status-rejected';
    }
  }

  onClear(violation: IWorkTimeViolation) {
    this.clearForm.reset();
    this.violationToClear = violation;
  }

  onCancelClear() {
    this.violationToClear = null;
  }

  onConfirmClear() {
    if (this.clearForm.invalid) {
      this.clearForm.markAllAsTouched();
      return;
    }

    this.workTimeViolationService.clearWorkTimeViolation({
      id: this.violationToClear.id,
      remark: this.clearForm.value.remark?.trim()
    })
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.violationToClear = null;
        this.appNotificationService.notification$.next({
          message: translate('time-entries.workTimeViolations.cleared'),
          style: 'success'
        });
        this.updateList();
      });
  }

  private resetPaging() {
    this.searchRequest.pageNumber = 1;
    this.gridState.skip = 0;
  }

  private toShort(timeSpan: string | null | undefined): string {
    return TimeSpan.parse(timeSpan ?? '00:00:00').toStringShort();
  }
}
